import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, PiggyBank, Landmark, FileWarning, FileDown, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { API, selectCls, eur, btnGhost, btnRed } from "./shared";

const MONTH_LABELS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

const Kpi = ({ icon: Icon, label, value, tone = "text-white", testid }) => (
  <div data-testid={testid} className="bg-[#1e2226] border border-white/10 rounded-2xl p-5">
    <div className="flex items-center gap-2 text-white/40 text-[11px] uppercase tracking-[0.18em] mb-2">
      <Icon size={14} /> {label}
    </div>
    <p className={`font-display font-bold text-2xl ${tone}`}>{value}</p>
  </div>
);

export const Overview = ({ onPrintReport }) => {
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = useCallback(() => {
    axios.get(`${API}/accounting/summary`, { withCredentials: true })
      .then(({ data }) => setSummary(data))
      .catch(() => {});
  }, []);

  useEffect(load, [load]);

  const chartData = (summary?.months || []).map((m) => {
    const [y, mo] = m.month.split("-");
    return { name: `${MONTH_LABELS[parseInt(mo, 10) - 1]} ${y.slice(2)}`, Einnahmen: m.einnahmen, Ausgaben: m.ausgaben };
  });

  const years = [...new Set((summary?.months || []).map((m) => m.month.slice(0, 4)).concat([String(new Date().getFullYear())]))].sort().reverse();

  const printReport = async () => {
    const [y, mo] = reportMonth.split("-");
    try {
      const [{ data: txs }, { data: receipts }, { data: invoices }] = await Promise.all([
        axios.get(`${API}/transactions`, { withCredentials: true }),
        axios.get(`${API}/receipts`, { withCredentials: true }),
        axios.get(`${API}/invoices`, { withCredentials: true }),
      ]);
      const prefix = reportMonth;
      onPrintReport({
        year: y,
        month: mo,
        transactions: txs.filter((t) => (t.date || "").startsWith(prefix)).sort((a, b) => a.date.localeCompare(b.date)),
        receipts: receipts.filter((r) => (r.uploaded_at || "").startsWith(prefix)),
        invoices,
      });
    } catch {
      // still print with empty data
      onPrintReport({ year: y, month: mo, transactions: [], receipts: [], invoices: [] });
    }
  };

  if (!summary) {
    return <div className="text-white/40 text-sm py-10 text-center" data-testid="overview-loading">Lade Buchhaltungs-Daten …</div>;
  }

  return (
    <div data-testid="accounting-overview">
      {/* KPI-Karten */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <Kpi icon={TrendingUp} label="Einnahmen (netto)" value={eur(summary.einnahmen)} tone="text-emerald-300" testid="kpi-einnahmen" />
        <Kpi icon={TrendingDown} label="Ausgaben (netto)" value={eur(summary.ausgaben)} tone="text-[#ff8090]" testid="kpi-ausgaben" />
        <Kpi icon={PiggyBank} label="Gewinn vor Steuern" value={eur(summary.gewinn_vor_steuer)} tone={summary.gewinn_vor_steuer >= 0 ? "text-emerald-300" : "text-[#ff8090]"} testid="kpi-gewinn" />
        <Kpi icon={Landmark} label="USt-Zahllast" value={eur(summary.zahllast)} tone={summary.zahllast >= 0 ? "text-amber-300" : "text-emerald-300"} testid="kpi-zahllast" />
        <Kpi icon={FileText} label="Offene Rechnungen" value={eur(summary.offene_rechnungen)} testid="kpi-offen" />
        <Kpi icon={FileWarning} label="Überfällig" value={eur(summary.ueberfaellige_rechnungen)} tone={summary.ueberfaellige_rechnungen > 0 ? "text-[#ff8090]" : "text-white"} testid="kpi-ueberfaellig" />
      </div>

      {/* Diagramm */}
      <div className="bg-[#1e2226] border border-white/10 rounded-2xl p-6 mb-8" data-testid="overview-chart">
        <p className="font-display text-white/40 text-xs uppercase tracking-[0.2em] mb-5">Einnahmen vs. Ausgaben pro Monat (netto)</p>
        {chartData.length === 0 ? (
          <p className="text-white/35 text-sm text-center py-10">Noch keine Buchungen – das Diagramm füllt sich mit deinen ersten Ein-/Ausgaben.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toLocaleString("de-DE")}k` : v)} />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.05)" }}
                contentStyle={{ background: "#14171a", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, fontSize: 13 }}
                labelStyle={{ color: "#fff" }}
                formatter={(v) => eur(v)}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Einnahmen" fill="#34d399" radius={[5, 5, 0, 0]} maxBarSize={38} />
              <Bar dataKey="Ausgaben" fill="#e2001a" radius={[5, 5, 0, 0]} maxBarSize={38} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Exporte für die Steuerberaterin */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#1e2226] border border-white/10 rounded-2xl p-6" data-testid="csv-export-card">
          <p className="font-display text-white/40 text-xs uppercase tracking-[0.2em] mb-2">CSV-Export für die Steuerberaterin</p>
          <p className="text-white/50 text-xs mb-4">Alle Buchungen mit Netto, MwSt., Brutto, Rechnungsbezug und Beleg-Anzahl – direkt in Excel öffnen.</p>
          <div className="flex flex-wrap gap-3">
            <select data-testid="csv-year" value={year} onChange={(e) => setYear(e.target.value)} className={`${selectCls} !w-28`}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <a data-testid="csv-download" href={`${API}/accounting/export.csv?year=${year}`} className={btnGhost}>
              <FileDown size={14} /> CSV herunterladen
            </a>
            <a data-testid="datev-download" href={`${API}/accounting/export-datev.csv?year=${year}`} className={btnRed}>
              <FileDown size={14} /> DATEV-Datei (SKR03)
            </a>
          </div>
          <p className="text-white/35 text-[11px] mt-3">Die DATEV-Datei ist ein Buchungsstapel im EXTF-Format – deine Steuerberaterin kann sie direkt in DATEV Rechnungswesen importieren (Kontenrahmen SKR03).</p>
        </div>
        <div className="bg-[#1e2226] border border-white/10 rounded-2xl p-6" data-testid="report-card">
          <p className="font-display text-white/40 text-xs uppercase tracking-[0.2em] mb-2">Monatliche Übersicht als PDF</p>
          <p className="text-white/50 text-xs mb-4">Druckfertiger Monatsbericht mit allen Buchungen, Summen und Beleg-Liste.</p>
          <div className="flex gap-3">
            <input
              data-testid="report-month"
              type="month"
              value={reportMonth}
              onChange={(e) => setReportMonth(e.target.value)}
              className="bg-white/5 border border-white/15 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#e2001a] [color-scheme:dark]"
            />
            <button data-testid="report-print" onClick={printReport} className={btnRed}>
              <FileText size={14} /> Bericht drucken
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
